import { TypedDocumentNode, gql } from '@apollo/client';
import { useEffect, useState } from 'react';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { myCartFragment } from '@sorare/core/src/contexts/currentUser/queries';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import type {
  CartBuyerQuery,
  CartBuyerQueryVariables,
} from './__generated__/usePollCartBought.graphql';

const CART_BUYER_QUERY = gql`
  query CartBuyerQuery(
    $id: String!
    $sport: Sport!
    $withCollectionPredictions: Boolean = true
  ) {
    tokens {
      primaryOffer(id: $id) {
        id
        buyer {
          slug
        }
      }
    }
    currentUser {
      slug
      ...CurrentUserProvider_myCart
    }
  }
  ${myCartFragment}
` as TypedDocumentNode<CartBuyerQuery, CartBuyerQueryVariables>;

export const usePollCartBought = (
  enabled: boolean,
  sport: Sport,
  propsId: string | undefined,
  cb: (successOfferId: string | false) => void
) => {
  const [id, setId] = useState<string | undefined>(propsId);
  const { currentUser } = useCurrentUserContext();

  const { data } = useQuery(CART_BUYER_QUERY, {
    variables: {
      id: idFromObject(id!),
      sport,
    },
    pollInterval: enabled ? 1000 : 0,
    skip: !enabled || !id,
  });

  const { primaryOffer } = data?.tokens || {};

  useEffect(() => {
    const offerId =
      !!currentUser &&
      !!primaryOffer?.buyer &&
      primaryOffer?.buyer.slug === currentUser.slug &&
      primaryOffer.id;
    if (enabled) {
      if (offerId) {
        cb(offerId);
        setId(undefined);
      }
    }
  }, [cb, enabled, currentUser, primaryOffer?.buyer, primaryOffer?.id, id]);

  if (!id && propsId) {
    setId(propsId);
  }
  if (propsId && id && id !== propsId) {
    setId(propsId);
  }
};
